import React, { useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Newspaper, Upload, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CitizenReporter = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location: '',
    news_description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/api/citizen-reports`, formData);
      setSubmitted(true);
      setFormData({ name: '', phone: '', location: '', news_description: '' });
    } catch (error) {
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <CardContent className="pt-12 pb-12">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">সফলভাবে জমা হয়েছে!</h2>
              <h3 className="text-xl text-gray-700 mb-4">Successfully Submitted!</h3>
              <p className="text-gray-600 mb-6">
                আপনার সংবাদ প্রতিবেদন সফলভাবে জমা হয়েছে। আমাদের দল শীঘ্রই এটি পর্যালোচনা করবে।
              </p>
              <p className="text-gray-600 mb-6">
                Your news report has been submitted successfully. Our team will review it shortly.
              </p>
              <Button onClick={() => setSubmitted(false)} className="bg-red-600 hover:bg-red-700">
                আরেকটি প্রতিবেদন জমা দিন / Submit Another Report
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-red-600 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <Newspaper className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-2">নাগরিক প্রতিবেদক</h1>
          <h2 className="text-2xl">Citizen Reporter</h2>
          <p className="mt-4 text-lg">আপনার এলাকার সংবাদ আমাদের জানান | Share news from your area</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>সংবাদ জমা দিন / Submit News</CardTitle>
              <CardDescription>
                আপনার এলাকার গুরুত্বপূর্ণ ঘটনা, সমস্যা বা সংবাদ আমাদের সাথে শেয়ার করুন
                <br />
                Share important events, issues or news from your area with us
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label>আপনার নাম / Your Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="নাম লিখুন / Enter your name"
                    required
                  />
                </div>

                <div>
                  <Label>ফোন নম্বর / Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="9876543210"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    আমরা যাচাইয়ের জন্য আপনার সাথে যোগাযোগ করতে পারি
                    <br />
                    We may contact you for verification
                  </p>
                </div>

                <div>
                  <Label>স্থান / Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="আগরতলা, ত্রিপুরা / Agartala, Tripura"
                    required
                  />
                </div>

                <div>
                  <Label>সংবাদ বিবরণ / News Description *</Label>
                  <Textarea
                    value={formData.news_description}
                    onChange={(e) => setFormData({ ...formData, news_description: e.target.value })}
                    rows={10}
                    placeholder="বিস্তারিত সংবাদ লিখুন... / Write detailed news description..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    কি ঘটেছে, কোথায়, কখন - সব বিস্তারিত লিখুন
                    <br />
                    Please provide all details: What happened, where, when
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">📝 নির্দেশিকা / Guidelines:</h4>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>সত্য এবং নির্ভরযোগ্য তথ্য প্রদান করুন / Provide truthful and accurate information</li>
                    <li>ব্যক্তিগত মতামত নয়, ঘটনার বিবরণ দিন / Provide facts, not personal opinions</li>
                    <li>মিথ্যা বা বিভ্রান্তিকর তথ্য দেবেন না / Do not provide false or misleading information</li>
                    <li>আমরা প্রকাশের আগে যাচাই করব / We will verify before publishing</li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-lg py-6"
                >
                  {submitting ? 'জমা হচ্ছে... / Submitting...' : 'সংবাদ জমা দিন / Submit News'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-sm text-gray-600">
            <p>আপনার পরিচয় গোপন রাখা হবে যদি আপনি চান</p>
            <p>Your identity will be kept confidential if requested</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CitizenReporter;
